import React, { createContext, useCallback, useContext, useRef } from "react";
import { GET, GET_V2, POST, POST_V2 } from "config/api.config";
import { normalizeAttendanceStatusOptions } from "model/AttendanceModel";
import { resolveAsyncTeamFilterPageName } from "helpers/metadataFilterHelpers";

const FILTER_API_ENDPOINT_MAP = {
    get_status_options: "common/status",
    get_order_status: "common/order-status",
    get_job_category_options: "common/job-category",
    hire_status_option: "common/order-status",
    get_profile_status: "common/profile-status",
    get_job_options: "common/job",
    get_reasons_options: "common/reasons",
    get_source_options: "common/source",
    get_yes_no: "common/yes-no",
    /** Client Register / filter metadata — Plan dropdown (`planId`). GET V2 `common/plan`. */
    get_plan_options: "common/plan",
    /** Client Review — Status toolbar dropdown (`statusId`). GET `common/client-review-status`. */
    get_client_review_status_options: "common/client-review-status",
    /** RTR Acknowledgement — Decision filter (`acknowledgeType`). */
    get_rtrdecision_options: "common/rtr-decision-options",
    /** Call executive schedule / team filters (`AllCommonFilterModal` metadata `api`). */
    get_team_options: "common/team",
    get_attendance_status_options: "common/attendance-status",
    get_call_status: "common/call-status",
    get_call_status_two: "common/call-status-two",
    /** Task planner — Select AE filter */
    get_account_executive_users: "common/account-executive-users",
    /** Candidate hire status — AllCommonFilterModal metadata `api` keys */
    get_hire_status_hire_status_options: "common/hire-status-hire-status",
    get_hire_status_client_paid_options: "HireStatus/options/client-paid",
    /** AI Recruiter log — notification type filter */
    get_airecruiter_notification_types: "common/ai-recruiter-notification-types",
    /** Bad Fit list — resolution reason filter */
    get_bad_fit_resolution_reason_options: "common/bad-fit-resolution-reasons",
};

/**
 * When metadata `api` is null, infer dropdown endpoint from `field`.
 * e.g. `jobId` → `get_job_options` → POST `common/job`.
 */
const FILTER_FIELD_DEFAULT_API_MAP = {
    jobId: "get_job_options",
};

/** Resolve dropdown api key from metadata filter definition (`api`, or field fallback). */
export const resolveMetadataDropdownApiKey = (definition = {}) => {
    const apiKey = String(definition?.api ?? "").trim();
    if (apiKey) return apiKey;
    const fieldKey = String(definition?.field ?? "").trim();
    return FILTER_FIELD_DEFAULT_API_MAP[fieldKey] || "";
};

const PAID_STATUS_FILTER_OPTIONS = [
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Pending" },
    { value: "replacement", label: "Replacement" },
];

const FILTER_API_METHOD_MAP = {
    get_job_options: "POST",
    get_team_options: "POST_V2",
    get_plan_options: "GET_V2",
};

const normalizeOptions = (payload) => {
    const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.records)
                ? payload.records
                : Array.isArray(payload?.rows)
                    ? payload.rows
                    : [];

    return list
        .map((item) => {
            const value =
                item?.value ??
                item?.id ??
                item?._id ??
                item?.key ??
                item?.code ??
                "";
            const label =
                item?.label ??
                item?.display ??
                item?.data ??
                item?.reason ??
                item?.name ??
                item?.title ??
                item?.text ??
                item?.value ??
                item?.id ??
                "";
            if (value === "" && label === "") return null;
            return {
                value,
                label: String(label),
            };
        })
        .filter(Boolean);
};

const buildDropdownCacheKey = (apiKey = "", options = {}) => {
    if (apiKey === "get_team_options") {
        const pageName = resolveAsyncTeamFilterPageName(options?.pageName);
        return pageName ? `${apiKey}:${pageName}` : apiKey;
    }
    return apiKey;
};

const fetchOptionsByApiKeyUncached = async (apiKey = "", options = {}) => {
    if (apiKey === "paid_status_option") {
        return PAID_STATUS_FILTER_OPTIONS;
    }
    const endpoint = FILTER_API_ENDPOINT_MAP[apiKey];
    if (!endpoint) return [];
    const method = FILTER_API_METHOD_MAP[apiKey] || "GET";
    try {
        let response;
        if (method === "POST_V2") {
            if (apiKey === "get_team_options") {
                const pageName = resolveAsyncTeamFilterPageName(options?.pageName);
                if (!pageName) return [];
                response = await POST_V2(endpoint, { pageName });
            } else {
                response = await POST_V2(endpoint, {});
            }
        } else if (method === "POST") {
            response = await POST(endpoint, {});
        } else if (method === "GET_V2") {
            response = await GET_V2(endpoint);
        } else {
            response = await GET(endpoint);
        }
        if (apiKey === "get_attendance_status_options") {
            return normalizeAttendanceStatusOptions(response);
        }
        const payload = response?.data ?? response;
        return normalizeOptions(payload);
    } catch (error) {
        return [];
    }
};

export const CommonFilterOptionsContext = createContext(null);

/**
 * Whether a metadata response should be cached for the SPA session.
 * Supports both `{ success: true, data }` and GET payloads shaped like `{ data: { table, filters } }`.
 */
const isMetadataResponseCacheable = (response) => {
    if (response == null || typeof response !== "object") return false;
    if (response.success === true) return true;
    if (response.success === false) return false;
    const st = String(response.status ?? "").toLowerCase();
    if (st === "error" || st === "failure") return false;
    const d = Object.prototype.hasOwnProperty.call(response, "data") ? response.data : response;
    if (!d || typeof d !== "object") return false;
    const cols = d.table?.columns;
    if (Array.isArray(cols)) return true;
    if (d.table && typeof d.table === "object") return true;
    if (Array.isArray(d.filters)) return true;
    return false;
};

/**
 * In-memory caches (session scope; cleared on hard refresh):
 * - `common/*` dropdown options for AllCommonFilterModal
 * - Table / filter metadata (`fetch*TableAndFilterSchema`) for feature contexts
 */
export const CommonFilterOptionsProvider = ({ children }) => {
    const cacheRef = useRef({});
    const inflightRef = useRef({});
    const metadataCacheRef = useRef({});
    const metadataInflightRef = useRef({});

    const getDropdownOptionsByApiKey = useCallback(async (apiKey, options = {}) => {
        if (!apiKey) return [];
        const cacheKey = buildDropdownCacheKey(apiKey, options);
        if (Object.prototype.hasOwnProperty.call(cacheRef.current, cacheKey)) {
            return cacheRef.current[cacheKey];
        }
        if (inflightRef.current[cacheKey]) {
            return inflightRef.current[cacheKey];
        }
        const promise = fetchOptionsByApiKeyUncached(apiKey, options)
            .then((opts) => {
                cacheRef.current[cacheKey] = opts;
                delete inflightRef.current[cacheKey];
                return opts;
            })
            .catch(() => {
                delete inflightRef.current[cacheKey];
                cacheRef.current[cacheKey] = [];
                return [];
            });
        inflightRef.current[cacheKey] = promise;
        return promise;
    }, []);

    /**
     * @param {string} cacheKey stable id, e.g. "metadata:jobCategory"
     * @param {() => Promise<any>} requestFn returns API response (same shape as direct model call)
     */
    const fetchMetadataSchema = useCallback(async (cacheKey, requestFn) => {
        if (!cacheKey || typeof requestFn !== "function") {
            return undefined;
        }
        const hit = metadataCacheRef.current[cacheKey];
        if (hit !== undefined) {
            return hit;
        }
        const inflight = metadataInflightRef.current[cacheKey];
        if (inflight) {
            return inflight;
        }
        const promise = requestFn()
            .then((response) => {
                if (isMetadataResponseCacheable(response)) {
                    metadataCacheRef.current[cacheKey] = response;
                }
                return response;
            })
            .catch((err) => {
                throw err;
            })
            .finally(() => {
                delete metadataInflightRef.current[cacheKey];
            });
        metadataInflightRef.current[cacheKey] = promise;
        return promise;
    }, []);

    return (
        <CommonFilterOptionsContext.Provider
            value={{ getDropdownOptionsByApiKey, fetchMetadataSchema }}
        >
            {children}
        </CommonFilterOptionsContext.Provider>
    );
};

export const useCommonFilterOptions = () => {
    const ctx = useContext(CommonFilterOptionsContext);
    if (!ctx) {
        throw new Error("useCommonFilterOptions must be used within CommonFilterOptionsProvider");
    }
    return ctx;
};
